(() => {
  if (customElements.get('thp-product-builder')) return;

  const sectionId = 'thp-product-builder-cart';
  const builders = new Set();
  let cartState = null;
  let requestVersion = 0;
  let cartRequest;
  let refreshTimer;

  function normalise(value) {
    return String(value ?? '').normalize('NFKC').trim().toLocaleLowerCase().replace(/\s+/g, ' ');
  }

  function cartFromSection(html) {
    if (typeof html !== 'string') return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-thp-builder-cart]');
    if (!element) return null;
    try { return JSON.parse(element.dataset.thpBuilderCart); } catch { return null; }
  }

  function applyCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    cartState = cart;
    builders.forEach(builder => builder.update());
  }

  async function refreshCart() {
    if (!builders.size) return;
    cartRequest?.abort();
    cartRequest = new AbortController();
    const version = ++requestVersion;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart?section_id=${sectionId}`, {
        headers: { Accept: 'text/html' }, cache: 'no-store', signal: cartRequest.signal
      });
      if (!response.ok) return;
      const next = cartFromSection(await response.text());
      if (version === requestVersion && next) applyCart(next);
    } catch { }
  }

  function scheduleCartRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 40);
  }

  document.addEventListener('cart:prepare-bundled-sections', event => {
    if (builders.size && Array.isArray(event.detail?.sections) && !event.detail.sections.includes(sectionId)) {
      event.detail.sections.push(sectionId);
    }
  });

  document.addEventListener('cart:change', event => {
    if (!builders.size) return;
    cartRequest?.abort();
    requestVersion++;
    const cart = event.detail?.cart;
    const context = cartFromSection(cart?.sections?.[sectionId]);
    if (context) applyCart(context);
    else {
      if (cart) applyCart(cart);
      scheduleCartRefresh();
    }
  });

  document.addEventListener('cart:refresh', scheduleCartRefresh);
  window.addEventListener('pageshow', event => { if (event.persisted) scheduleCartRefresh(); });
  window.addEventListener('focus', scheduleCartRefresh);

  class THPProductBuilder extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.selectedIds = new Set();
      this.buttonStates = new Map();
      this.bundleSelected = false;
      queueMicrotask(() => this.setup());
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      builders.delete(this);
      this.restoreButtons();
      this.form?.removeAttribute('data-thp-builder-selected');
      this.form?.removeAttribute('data-thp-builder-bundle');
      if (!builders.size) {
        cartRequest?.abort();
        clearTimeout(refreshTimer);
      }
    }

    setup() {
      if (!this.isConnected) return;
      try {
        this.config = JSON.parse(this.dataset.config);
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form || !('FormDataEvent' in window)) return;
        this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: this.config.currency
        });
        this.cards = Array.from(this.querySelectorAll('[data-builder-product]')).map(element => ({
          element, productId: Number(element.dataset.productId), variants: JSON.parse(element.dataset.variants),
          input: element.querySelector('[data-builder-extra]'), select: element.querySelector('[data-builder-extra-variant]'),
          price: element.querySelector('[data-builder-extra-price]'), image: element.querySelector('img')
        }));
      } catch { return; }
      const seen = new Set();
      this.cards.forEach(card => {
        card.duplicate = seen.has(card.productId);
        seen.add(card.productId);
      });
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('click', event => this.onClick(event), options);
      this.form.addEventListener('variant:change', event => {
        if (event.detail?.variant) this.latestVariant = event.detail.variant;
        this.update();
      }, options);
      this.form.addEventListener('product:rerender', () => queueMicrotask(() => {
        this.update();
        requestAnimationFrame(() => this.update());
      }), options);
      document.addEventListener('change', event => {
        if (event.target.form === this.form) this.update();
      }, options);
      document.addEventListener('input', event => {
        if (event.target.form === this.form && event.target.name === 'quantity') this.update();
      }, options);
      this.form.addEventListener('submit', event => {
        this.update();
        if (!this.validSelection()) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.showError(this.config.strings.quantityError);
        }
      }, { ...options, capture: true });
      this.form.addEventListener('formdata', event => this.addItems(event.formData), options);
      this.form.addEventListener('variant:add', () => {
        this.selectedIds.clear();
        queueMicrotask(() => this.update());
      }, options);
      this.form.addEventListener('reset', () => {
        this.selectedIds.clear(); this.bundleSelected = false;
        queueMicrotask(() => this.update());
      }, options);
      this.querySelectorAll('.thpb__products').forEach(row => row.addEventListener('scroll', () => this.updateArrows(), options));
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this);
      }
      if (!cartState) {
        try { cartState = JSON.parse(this.dataset.cart); } catch { cartState = { items: [] }; }
      }
      builders.add(this);
      this.update();
    }

    money(amount) { return this.formatter.format(amount / 100); }

    quantity() {
      const raw = Number(this.form.elements.namedItem('quantity')?.value || 1);
      return Number.isSafeInteger(raw) && raw > 0 ? raw : 0;
    }

    mainVariant() {
      const id = Number(this.form.elements.namedItem('id')?.value);
      const initial = this.config.mainVariants.find(variant => variant.id === id);
      if (!initial) return null;
      if (Number(this.latestVariant?.id) === id) return { ...initial, ...this.latestVariant };
      return initial;
    }

    bundleVariant(main) {
      if (!this.config.showBundle || !main) return null;
      if (main.bundleVariantId) return this.config.bundleVariants.find(variant => variant.id === Number(main.bundleVariantId)) || null;
      const source = main.options?.[this.config.mainOption - 1];
      if (source == null) return null;
      let wanted = normalise(source);
      for (const line of String(this.config.colourMap || '').split(/\r?\n/)) {
        const separator = line.indexOf('=');
        if (separator > -1 && normalise(line.slice(0, separator)) === wanted) {
          wanted = normalise(line.slice(separator + 1)); break;
        }
      }
      const matches = this.config.bundleVariants.filter(variant => normalise(variant.options?.[this.config.bundleOption - 1]) === wanted);
      return matches.length === 1 ? matches[0] : null;
    }

    basketIds() {
      const products = new Set(), variants = new Set(), included = new Set();
      const known = new Map();
      builders.forEach(builder => {
        if (builder.config.includedProductIds.length) known.set(Number(builder.config.productId), builder.config.includedProductIds);
        if (builder.config.bundleProductId && builder.config.bundleIncludedProductIds.length) {
          known.set(Number(builder.config.bundleProductId), builder.config.bundleIncludedProductIds);
        }
      });
      const walk = item => {
        const productId = Number(item.product_id);
        const variantId = Number(item.variant_id || item.id);
        if (productId) products.add(productId);
        if (variantId) variants.add(variantId);
        [...(item.included_product_ids || []), ...(known.get(productId) || [])].forEach(id => included.add(Number(id)));
        (item.components || item.item_components || []).forEach(walk);
      };
      (cartState?.items || []).forEach(walk);
      return { products, variants, included };
    }

    variantInBasket(card, variant, basket) {
      if (!this.config.hideInCart) return false;
      return basket.included.has(card.productId) ||
        (this.config.cartMatch === 'product' ? basket.products.has(card.productId) : basket.variants.has(variant.id));
    }

    quantityAllowed(variant, quantity) {
      if (!variant || !Number.isSafeInteger(quantity) || quantity < 1) return false;
      const minimum = Number(variant.min || variant.quantity_rule?.min || 1);
      const maximum = variant.max ?? variant.quantity_rule?.max;
      const increment = Number(variant.increment || variant.quantity_rule?.increment || 1);
      return quantity >= minimum && (maximum == null || quantity <= Number(maximum)) && (quantity - minimum) % increment === 0;
    }

    selectedExtras() {
      return this.cards.filter(card => !card.element.hidden && !card.included && card.variant && this.selectedIds.has(card.variant.id));
    }

    validSelection() {
      const main = this.bundleSelected ? this.currentBundle : this.mainVariant();
      const quantity = this.quantity();
      if (!main?.available || !this.quantityAllowed(main, quantity)) return false;
      return this.selectedExtras().every(card => this.quantityAllowed(card.variant, this.config.scaleExtras ? quantity : 1));
    }

    onChange(event) {
      this.showError('');
      if (event.target.matches('[data-builder-path]')) {
        this.bundleSelected = event.target.value === 'bundle';
      } else {
        const card = this.cards.find(item => item.element.contains(event.target));
        if (card) {
          if (event.target === card.select) {
            if (card.variant) this.selectedIds.delete(card.variant.id);
            if (card.select.value) this.selectedIds.add(Number(card.select.value));
          } else if (event.target === card.input && card.variant) {
            if (card.input.checked) this.selectedIds.add(card.variant.id);
            else this.selectedIds.delete(card.variant.id);
          }
        }
      }
      this.update();
    }

    onClick(event) {
      const button = event.target.closest('[data-builder-scroll]');
      if (!button || !this.contains(button)) return;
      const row = button.closest('[data-builder-group]').querySelector('.thpb__products');
      row.scrollBy({
        left: row.clientWidth * .75 * Number(button.dataset.builderScroll),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    showError(message) {
      const error = this.querySelector('[data-builder-error]');
      if (!error) return;
      error.textContent = message; error.hidden = !message;
    }

    update() {
      if (!this.isConnected || !this.form || !this.config) return;
      const main = this.mainVariant();
      const bundle = this.bundleVariant(main);
      this.currentBundle = bundle;
      const sellingPlan = this.form.elements.namedItem('selling_plan')?.value;
      const bundleAvailable = !!bundle?.available && !sellingPlan;
      if (this.bundleSelected && !bundleAvailable) {
        this.bundleSelected = false;
        const status = this.querySelector('[data-builder-status]');
        status.textContent = this.config.strings.bundleReset; status.hidden = false;
      }
      this.querySelectorAll('[data-builder-path]').forEach(input => {
        input.checked = (input.value === 'bundle') === this.bundleSelected;
        if (input.value === 'bundle') input.disabled = !bundleAvailable;
      });
      const mainPrice = this.querySelector('[data-builder-main-price]');
      if (mainPrice) mainPrice.textContent = main ? this.money(main.price) : '';
      const bundlePrice = this.querySelector('[data-builder-bundle-price]');
      if (bundlePrice) bundlePrice.textContent = bundle ? this.money(bundle.price) : '';
      const availability = this.querySelector('[data-builder-bundle-availability]');
      if (availability) availability.textContent = bundleAvailable ? '' : (sellingPlan || !bundle ? this.config.strings.bundleOptions : this.config.strings.bundleUnavailable);
      this.querySelector('[data-builder-bundle-card]')?.classList.toggle('thpb__path--unavailable', !bundleAvailable);
      const badge = this.querySelector('[data-builder-upgrade]');
      if (badge) {
        badge.hidden = !bundle || !main;
        if (bundle && main) badge.textContent = bundle.price > main.price ? this.config.strings.upgrade.replace('[amount]', this.money(bundle.price - main.price)) : this.config.strings.bundleLabel;
      }
      const basket = this.basketIds();
      const baseIncluded = new Set(this.config.includedProductIds.map(Number));
      const upgradeIncluded = new Set(this.bundleSelected ? this.config.bundleIncludedProductIds.map(Number) : []);
      this.cards.forEach(card => {
        const available = card.variants.filter(variant => variant.available && !this.variantInBasket(card, variant, basket));
        card.included = upgradeIncluded.has(card.productId);
        card.element.hidden = card.duplicate || baseIncluded.has(card.productId) || available.length === 0;
        if (card.select) {
          Array.from(card.select.options).forEach(option => {
            if (!option.value) return;
            const variant = available.find(item => item.id === Number(option.value));
            option.disabled = !variant; option.hidden = !variant;
          });
          if (card.select.value && !available.some(variant => variant.id === Number(card.select.value))) {
            this.selectedIds.delete(Number(card.select.value)); card.select.value = '';
          }
          card.variant = available.find(variant => variant.id === Number(card.select.value)) || null;
          card.select.disabled = card.included;
        } else card.variant = available[0] || null;
        card.variants.forEach(variant => {
          if (this.variantInBasket(card, variant, basket)) this.selectedIds.delete(variant.id);
        });
        card.input.checked = card.included || !!card.variant && this.selectedIds.has(card.variant.id);
        card.input.disabled = card.included || !card.variant;
        card.element.classList.toggle('thpb__product--included', card.included);
        if (card.included) card.price.textContent = this.config.strings.included;
        else if (card.variant && !this.quantityAllowed(card.variant, this.config.scaleExtras ? this.quantity() : 1)) card.price.textContent = this.config.strings.quantityUnavailable;
        else if (card.variant) card.price.textContent = '+ ' + this.money(card.variant.price);
        else if (available.length) card.price.textContent = this.config.strings.from.replace('[amount]', this.money(Math.min(...available.map(variant => variant.price))));
        else card.price.textContent = this.config.strings.unavailable;
        if (card.image && !card.element.hasAttribute('data-builder-custom-thumbnail') && card.variant?.image && card.image.dataset.variantImage !== String(card.variant.id)) {
          card.image.src = card.variant.image; card.image.removeAttribute('srcset'); card.image.dataset.variantImage = String(card.variant.id);
        }
      });
      this.querySelectorAll('[data-builder-group]').forEach(group => {
        group.hidden = !Array.from(group.querySelectorAll('[data-builder-product]')).some(card => !card.hidden);
      });
      const visibleExtras = Array.from(this.querySelectorAll('[data-builder-group]')).some(group => !group.hidden);
      this.querySelector('[data-builder-extras]').hidden = !visibleExtras;
      this.querySelector('[data-builder-note]').hidden = !visibleExtras && !this.bundleSelected;
      this.hidden = !this.config.showBundle && !visibleExtras;
      const selected = this.bundleSelected || this.selectedExtras().length > 0;
      this.form.toggleAttribute('data-thp-builder-selected', selected);
      this.form.toggleAttribute('data-thp-builder-bundle', this.bundleSelected && bundleAvailable);
      this.updateButtons(main, bundle, selected);
      requestAnimationFrame(() => { if (this.isConnected) this.updateArrows(); });
    }

    updateButtons(main, bundle, selected) {
      const target = this.bundleSelected ? bundle : main;
      const extraTotal = this.selectedExtras().reduce((sum, card) => sum + card.variant.price * (this.config.scaleExtras ? this.quantity() : 1), 0);
      const total = (target?.price || 0) * this.quantity() + extraTotal;
      Array.from(this.form.elements).filter(button => button.tagName === 'BUTTON' && button.type === 'submit' && button.closest('buy-buttons')).forEach(button => {
        if (!this.buttonStates.has(button)) this.buttonStates.set(button, {
          text: button.textContent.trim(), disabled: button.disabled,
          hiddenParent: button.parentElement?.tagName === 'SPAN' && button.parentElement.hidden ? button.parentElement : null
        });
        const original = this.buttonStates.get(button);
        if (button.getAttribute('aria-busy') === 'true') return;
        if (this.bundleSelected && !main?.available) {
          button.disabled = !target?.available;
          original.forcedAvailability = true;
          if (original.hiddenParent) original.hiddenParent.hidden = false;
        } else {
          if (original.forcedAvailability) {
            button.disabled = original.disabled;
            original.forcedAvailability = false;
          }
          if (original.hiddenParent) original.hiddenParent.hidden = true;
        }
        button.textContent = selected && target?.available ? this.config.strings.total.replace('[action]', this.config.strings.addToCart).replace('[amount]', this.money(total)) : original.text;
      });
    }

    restoreButtons() {
      this.buttonStates?.forEach((state, button) => {
        button.textContent = state.text; button.disabled = state.disabled;
        if (state.hiddenParent) state.hiddenParent.hidden = true;
      });
    }

    updateArrows() {
      this.querySelectorAll('[data-builder-group]').forEach(group => {
        if (group.hidden) return;
        const row = group.querySelector('.thpb__products');
        const arrows = group.querySelector('.thpb__arrows');
        arrows.hidden = row.scrollWidth <= row.clientWidth + 2;
        const position = Math.abs(row.scrollLeft);
        group.querySelector('[data-builder-scroll="-1"]').disabled = position < 2;
        group.querySelector('[data-builder-scroll="1"]').disabled = position >= row.scrollWidth - row.clientWidth - 2;
      });
    }

    addItems(data) {
      const extras = this.selectedExtras();
      if ((!this.bundleSelected && !extras.length) || !this.validSelection()) return;
      const main = this.mainVariant();
      const target = this.bundleSelected ? this.currentBundle : main;
      const indices = Array.from(data.keys()).map(key => /^items\[(\d+)\]\[id\]$/.exec(key)).filter(Boolean).map(match => Number(match[1]));
      let mainIndex;
      if (indices.length) mainIndex = indices.find(index => Number(data.get(`items[${index}][id]`)) === main.id);
      else {
        if (Number(data.get('id')) !== main.id) return;
        mainIndex = 0;
        for (const [key, value] of Array.from(data.entries())) {
          let destination;
          if (key === 'id' || key === 'quantity' || key === 'selling_plan') destination = `items[0][${key}]`;
          else if (key.startsWith('properties[')) destination = `items[0][properties]${key.slice(10)}`;
          if (destination) { data.append(destination, value); data.delete(key); }
        }
      }
      if (mainIndex == null) return;
      data.set(`items[${mainIndex}][id]`, String(target.id));
      data.set(`items[${mainIndex}][quantity]`, String(this.quantity()));
      const present = new Set(indices.map(index => Number(data.get(`items[${index}][id]`))));
      present.add(target.id);
      let next = indices.length ? Math.max(...indices) + 1 : 1;
      extras.forEach(card => {
        if (present.has(card.variant.id)) return;
        data.set(`items[${next}][id]`, String(card.variant.id));
        data.set(`items[${next}][quantity]`, String(this.config.scaleExtras ? this.quantity() : 1));
        present.add(card.variant.id); next++;
      });
    }
  }

  customElements.define('thp-product-builder', THPProductBuilder);
})();
